/**
 * ProShellFab — iter87.189 → iter87.190 restyle.
 *
 * Matches the DashboardMenu pattern used by league admin/player views:
 *   - Volt-blue round FAB (w-14 h-14) at bottom-right (iter87.393),
 *     portalled to body so no backdrop-filter ancestor can hijack
 *     fixed positioning.
 *   - Auto-hides on scroll-down, returns on scroll-up (same UX contract
 *     as `dashboard-menu-fab`).
 *   - Tap → opens a GlassSheet (bottom sheet on mobile, floating panel
 *     on desktop) with Row-style action entries: icon in a small
 *     rounded circle + bold label + faint sub-line.
 *
 * Four actions carry over from the previous implementation, unchanged:
 *   - Start a Rivalry
 *   - How this works
 *   - Report an issue
 *   - Cancel Pro+  (danger styling, matches Row's `danger` variant)
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Menu, Plus, HelpCircle, MessageCircleWarning, XCircle } from "lucide-react";
import GlassSheet from "@/components/onui/GlassSheet";

const Row = ({ icon: Icon, label, sub, onClick, testId, danger }) => {
    const cls = `w-full flex items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors active:scale-[0.98] ${
        danger ? "text-rose-400 hover:bg-rose-500/10" : "hover:bg-white/[0.06]"
    }`;
    return (
        <button type="button" data-testid={testId} onClick={onClick} className={cls}>
            <span
                className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                    danger ? "bg-rose-500/10" : "bg-white/[0.06]"
                }`}
            >
                {Icon && <Icon className="w-4 h-4" />}
            </span>
            <span className="flex-1 min-w-0">
                <span className="block font-display font-extrabold text-sm uppercase tracking-tight truncate">{label}</span>
                {sub && <span className="block text-[11px] text-zinc-400 truncate">{sub}</span>}
            </span>
        </button>
    );
};

export default function ProShellFab({ onAction }) {
    const [open, setOpen] = useState(false);
    const [fabHidden, setFabHidden] = useState(false);

    // FAB auto-hides on scroll down, returns on scroll up — same UX
    // contract as the league dashboard menu FAB.
    useEffect(() => {
        let last = window.scrollY;
        const onScroll = () => {
            const y = window.scrollY;
            setFabHidden(y > last && y > 80);
            last = y;
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const dispatch = (key) => {
        setOpen(false);
        onAction?.(key);
    };

    return (
        <>
            {/* Trigger — cyber FAB, portalled to body so no
                backdrop-filter ancestor becomes its containing block. */}
            {createPortal(
                <button
                    type="button"
                    data-testid="pro-shell-fab-toggle"
                    onClick={() => setOpen(true)}
                    aria-label="Open Pro+ menu"
                    className={`fixed bottom-5 right-4 z-40 w-14 h-14 rounded-full bg-volt text-black border-2 border-black shadow-[0_10px_30px_rgba(0,122,255,0.35)] flex items-center justify-center transition-all duration-300 active:scale-90 ${
                        fabHidden && !open ? "translate-y-24 opacity-0 pointer-events-none" : ""
                    }`}
                >
                    <Menu className="w-6 h-6" strokeWidth={2.5} />
                </button>,
                document.body,
            )}

            <GlassSheet
                open={open}
                onClose={() => setOpen(false)}
                title="Pro+ Menu"
                testId="pro-shell-fab-sheet"
            >
                <div className="space-y-0.5" data-testid="pro-shell-fab-actions">
                    <Row
                        icon={Plus}
                        label="Start a Rivalry"
                        sub="Create a new league you'll administrate"
                        onClick={() => dispatch("start")}
                        testId="pro-shell-fab-action-start"
                    />
                    <Row
                        icon={HelpCircle}
                        label="How this works"
                        sub="Quick tour of everything on this page"
                        onClick={() => dispatch("howto")}
                        testId="pro-shell-fab-action-howto"
                    />
                    <Row
                        icon={MessageCircleWarning}
                        label="Report an issue"
                        sub="Send a note to the platform admin"
                        onClick={() => dispatch("report")}
                        testId="pro-shell-fab-action-report"
                    />
                    <Row
                        icon={XCircle}
                        label="Cancel Pro+"
                        sub="Keep access until your cycle ends"
                        onClick={() => dispatch("cancel")}
                        testId="pro-shell-fab-action-cancel"
                        danger
                    />
                </div>
            </GlassSheet>
        </>
    );
}
